import React, { useState, useMemo } from 'react';
import moment from 'moment';
import ReactDataGrid from '@inovua/reactdatagrid-community';
import '@inovua/reactdatagrid-community/index.css';
import SelectFilter from '@inovua/reactdatagrid-community/SelectFilter';
import DateFilter from '@inovua/reactdatagrid-community/DateFilter';
import { jsPDF } from 'jspdf';
import XLSX from 'sheetjs-style';
import 'jspdf-autotable';
import 'assets/css/table.css';
import dateformat from 'dateformat';

import common from '../../../commonData';
import NavMenu from './navbarArticles';

//---------- LISTE DETAILLEE DES ARTICLES LIVRES ET RETOURNES ----------
// Données à plat renvoyées par /api/sortie/livresretournes (une ligne = un article d'un bon de sortie)
function ArticlesLivresRetournes({ rows, isLoading, setIsSave, setMsg, setMsgravity }) {
    window.moment = moment;
    const [gridRef, setGridRef] = useState(null);

    function numberformat(data) {
        return new Intl.NumberFormat().format(data || 0);
    }

    // Normalisation des valeurs numériques (les colonnes calculées SQL arrivent en chaine) + statut
    const lignes = useMemo(() => {
        return (rows || []).map((row) => {
            const quantite = Number(row.quantite) || 0;
            const quantiteretour = Number(row.quantiteretour) || 0;
            const qtelivree = Number(row.qtelivree) || 0;

            return {
                ...row,
                prix: Number(row.prix) || 0,
                remise: Number(row.remise) || 0,
                quantite: quantite,
                quantiteretour: quantiteretour,
                qtelivree: qtelivree,
                montantlivre: Number(row.montantlivre) || 0,
                montantretour: Number(row.montantretour) || 0,
                statut: quantiteretour <= 0 ? 'Livré' : qtelivree <= 0 ? 'Retour total' : 'Retour partiel'
            };
        });
    }, [rows]);

    // La liste des clients du filtre est déduite des données affichées
    const listeClients = useMemo(() => {
        const noms = [...new Set(lignes.map((l) => l.client).filter((c) => c))].sort();
        return noms.map((c) => {
            return { id: c, label: c };
        });
    }, [lignes]);

    const filterValue = [
        { name: 'numbon', operator: 'contains', type: 'string', value: '' },
        { name: 'datesort', operator: 'inrange', type: 'date', value: '' },
        { name: 'client', operator: 'inlist', type: 'select', value: [] },
        { name: 'qrbarre', operator: 'contains', type: 'string', value: '' },
        { name: 'descriptionart', operator: 'contains', type: 'string', value: '' },
        { name: 'libellefam', operator: 'contains', type: 'string', value: '' },
        { name: 'libellemarq', operator: 'contains', type: 'string', value: '' },
        { name: 'engin', operator: 'contains', type: 'string', value: '' },
        { name: 'statut', operator: 'inlist', type: 'select', value: [] },
        { name: 'quantite', operator: 'gte', type: 'number', value: null },
        { name: 'quantiteretour', operator: 'gte', type: 'number', value: null },
        { name: 'qtelivree', operator: 'gte', type: 'number', value: null },
        { name: 'prix', operator: 'gte', type: 'number', value: null },
        { name: 'montantretour', operator: 'gte', type: 'number', value: null },
        { name: 'montantlivre', operator: 'gte', type: 'number', value: null }
    ];

    const columnss = [
        { name: 'numbon', header: 'N° bon', defaultWidth: 100, type: 'string', textAlign: 'center' },
        {
            name: 'datesort',
            header: 'Date sortie',
            defaultWidth: 130,
            type: 'date',
            dateFormat: 'DD-MM-YYYY',
            textAlign: 'center',
            filterEditor: DateFilter,
            filterEditorProps: (props, { index }) => {
                return {
                    dateFormat: 'DD-MM-YYYY',
                    placeholder: index === 1 ? 'Date fin' : 'Date debut'
                };
            },
            render: ({ value, cellProps: { dateFormat } }) => moment(value).format(dateFormat)
        },
        {
            name: 'client',
            header: 'Clients',
            defaultWidth: 170,
            type: 'string',
            filterEditor: SelectFilter,
            filterEditorProps: {
                multiple: true,
                wrapMultiple: false,
                dataSource: listeClients
            }
        },
        { name: 'qrbarre', header: 'Code', defaultWidth: 120, type: 'string' },
        { name: 'descriptionart', header: 'Articles', defaultWidth: 250, type: 'string' },
        { name: 'libellefam', header: 'Famille', defaultWidth: 130, type: 'string' },
        { name: 'libellemarq', header: 'Marque', defaultWidth: 120, type: 'string' },
        { name: 'engin', header: 'Engin', defaultWidth: 130, type: 'string' },
        {
            name: 'quantite',
            header: 'Qté initiale',
            defaultWidth: 110,
            textAlign: 'center',
            type: 'number',
            render: ({ value }) => numberformat(value)
        },
        {
            name: 'quantiteretour',
            header: 'Qté retournée',
            defaultWidth: 120,
            textAlign: 'center',
            type: 'number',
            render: ({ value }) => (value > 0 ? <p id="warning">{numberformat(value)}</p> : numberformat(value))
        },
        {
            name: 'qtelivree',
            header: 'Qté livrée',
            defaultWidth: 110,
            textAlign: 'center',
            type: 'number',
            render: ({ value }) => numberformat(value)
        },
        {
            name: 'statut',
            header: 'Statut',
            defaultWidth: 120,
            textAlign: 'center',
            type: 'string',
            filterEditor: SelectFilter,
            filterEditorProps: {
                multiple: true,
                wrapMultiple: false,
                dataSource: [
                    { id: 'Livré', label: 'Livré' },
                    { id: 'Retour partiel', label: 'Retour partiel' },
                    { id: 'Retour total', label: 'Retour total' }
                ]
            },
            render: ({ value }) => (value === 'Livré' ? <p id="successs">{value}</p> : <p id="warning">{value}</p>)
        },
        {
            name: 'prix',
            header: 'Prix vente',
            defaultWidth: 110,
            textAlign: 'center',
            type: 'number',
            render: ({ value }) => numberformat(value)
        },
        {
            name: 'montantretour',
            header: 'Mont. retour',
            defaultWidth: 120,
            textAlign: 'center',
            type: 'number',
            render: ({ value }) => numberformat(value)
        },
        {
            name: 'montantlivre',
            header: 'Mont. livré',
            defaultWidth: 120,
            textAlign: 'center',
            type: 'number',
            render: ({ value }) => numberformat(value)
        }
    ];

    const gridStyle = { minHeight: window.screen.height - 200 };

    function GetSortOrder(prop) {
        return function (a, b) {
            if (a[prop] > b[prop]) {
                return 1;
            } else if (a[prop] < b[prop]) {
                return -1;
            }
            return 0;
        };
    }

    // Les données exportées sont celles affichées (filtres de la grille appliqués)
    function donneesAffichees() {
        if (!gridRef || !gridRef.current || gridRef.current.data.length <= 0) {
            setMsg('Aucune donnée à exporter');
            setIsSave(true);
            setMsgravity('danger');
            return null;
        }
        return gridRef.current.data.sort(GetSortOrder('datesort'));
    }

    const exportCSVArticle = () => {
        //--------------- EXPORT VERS EXCEL ------------------
        const raw_data = donneesAffichees();
        if (raw_data === null) {
            return;
        }

        const datas = raw_data.map((row) => {
            return {
                numbon: row.numbon,
                date: dateformat(row.datesort, 'dd-mm-yyyy'),
                client: row.client,
                qrbarre: row.qrbarre,
                description: row.descriptionart,
                famille: row.libellefam,
                marque: row.libellemarq,
                engin: row.engin,
                quantite: row.quantite,
                quantiteretour: row.quantiteretour,
                qtelivree: row.qtelivree,
                prix: row.prix,
                montantretour: row.montantretour,
                montantlivre: row.montantlivre,
                statut: row.statut
            };
        });

        /* generate worksheet and workbook */
        const worksheet = XLSX.utils.json_to_sheet(datas);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Articles livrés-retournés');
        /* fix headers */
        XLSX.utils.sheet_add_aoa(
            worksheet,
            [
                [
                    'N° bon',
                    'Date sortie',
                    'Clients',
                    'Code',
                    'Articles',
                    'Famille',
                    'Marque',
                    'Engin',
                    'Qté initiale',
                    'Qté retournée',
                    'Qté livrée',
                    'Prix vente',
                    'Mont. retour',
                    'Mont. livré',
                    'Statut'
                ]
            ],
            { origin: 'A1' }
        );
        /* calculate column width */
        const max_width = datas.reduce((w, r) => Math.max(w, String(r.description || '').length), 10);
        worksheet['!cols'] = [{ wch: 12 }, { wch: 12 }, { wch: 25 }, { wch: 15 }, { wch: max_width }];
        XLSX.writeFile(workbook, 'articles-livres-retournes.xlsx');
    };

    //-------------IMPRIMER LA LISTE ----------------
    function printListeArticle() {
        const raw_data = donneesAffichees();
        if (raw_data === null) {
            return;
        }

        const doc = new jsPDF({
            orientation: 'l',
            unit: 'mm',
            format: 'a4',
            putOnlyUsedFonts: true
        });
        const width = doc.internal.pageSize.getWidth();
        const data = [];
        let totquantite = 0;
        let totretour = 0;
        let totlivre = 0;
        let totmontant = 0;

        common.ipapi == 'https://192.168.8.109:81' || common.ipapi == 'https://api.tracky-entrepot.com'
            ? doc.addImage(common.logo, 'png', 14, 10, 30, 30)
            : doc.addImage(common.logo1, 'png', 14, 10, 30, 30);

        doc.setFontSize(12);
        doc.setFillColor(61, 43, 31);
        doc.rect(65, 15, width - 80, 10, 'F');
        doc.setTextColor(255, 255, 255);
        doc.text('LISTE DES ARTICLES LIVRES ET RETOURNES', 90, 22);
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(
            'Période du: ' +
                dateformat(raw_data[0].datesort, 'dd-mm-yyyy') +
                ' au: ' +
                dateformat(raw_data[raw_data.length - 1].datesort, 'dd-mm-yyyy'),
            65,
            32
        );

        raw_data.forEach((row) => {
            totquantite += row.quantite;
            totretour += row.quantiteretour;
            totlivre += row.qtelivree;
            totmontant += row.montantlivre;
            data.push([
                { content: row.numbon },
                { content: dateformat(row.datesort, 'dd-mm-yyyy') },
                { content: row.client },
                { content: row.qrbarre },
                { content: row.descriptionart },
                { content: row.engin },
                { content: numberformat(row.quantite) },
                { content: numberformat(row.quantiteretour) },
                { content: numberformat(row.qtelivree) },
                { content: numberformat(row.prix) },
                { content: numberformat(row.montantlivre) },
                { content: row.statut }
            ]);
        });

        data.push([
            { content: 'TOTAL', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
            { content: numberformat(totquantite), styles: { fontStyle: 'bold' } },
            { content: numberformat(totretour), styles: { fontStyle: 'bold' } },
            { content: numberformat(totlivre), styles: { fontStyle: 'bold' } },
            { content: '' },
            { content: numberformat(totmontant), styles: { fontStyle: 'bold' } },
            { content: '' }
        ]);

        doc.autoTable({
            styles: {
                fillColor: [0, 0, 0],
                textColor: [255, 255, 255],
                halign: 'center',
                fontSize: 8,
                cellPadding: 0.75
            },
            columnStyles: {
                0: { halign: 'center', fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8 },
                1: { halign: 'center', fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8 },
                2: { halign: 'left', fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8 },
                3: { halign: 'center', fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8 },
                4: { halign: 'left', fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8 },
                5: { halign: 'left', fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8 },
                6: { halign: 'center', fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8 },
                7: { halign: 'center', fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8 },
                8: { halign: 'center', fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8 },
                9: { halign: 'right', fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8 },
                10: { halign: 'right', fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8 },
                11: { halign: 'center', fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8 }
            },
            theme: 'grid',
            startY: 38,
            head: [
                [
                    'N° bon',
                    'Date',
                    'Client',
                    'Code',
                    'Article',
                    'Engin',
                    'Qté initiale',
                    'Qté retour',
                    'Qté livrée',
                    'Prix vente',
                    'Mont. livré',
                    'Statut'
                ]
            ],
            body: data
        });

        const pageCount = doc.internal.getNumberOfPages();
        for (var i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(9);
            doc.text(
                'Page ' + String(i) + ' sur ' + String(pageCount),
                width - 15,
                doc.internal.pageSize.getHeight() - 10,
                null,
                null,
                'right'
            );
        }
        doc.save('articles-livres-retournes.pdf');
    }

    return (
        <div>
            <NavMenu printListeArticle={printListeArticle} exportCSVArticle={exportCSVArticle} />
            <ReactDataGrid
                idProperty="id"
                handle={setGridRef}
                style={gridStyle}
                loading={isLoading}
                reorderColumns={false}
                pagination
                pageSizes={[200, 400, 600, 800, 1000, lignes.length]}
                defaultLimit={400}
                showHoverRows={true}
                editable={false}
                showColumnMenuTool={false}
                defaultFilterValue={filterValue}
                columns={columnss}
                dataSource={lignes}
            />
        </div>
    );
}

export default ArticlesLivresRetournes;
